import { writable } from 'svelte/store';
import Keycloak from 'keycloak-js';
import { getBackendConfig } from '$lib/apis';
import { logger } from '$lib/utils/logger';

export const keycloak = writable<Keycloak | null>(null);

let keycloakInstance: Keycloak | null = null;
let initPromise: Promise<Keycloak | null> | null = null;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let tokenRefreshPromise: Promise<boolean> | null = null;

const scheduleRefresh = (kc: Keycloak, safetySeconds = 60) => {
	if (refreshTimer) clearTimeout(refreshTimer);

	const now = Date.now();
	const expMs = (kc.tokenParsed?.exp ?? 0) * 1000;
	const delay = Math.max(5_000, expMs - now - safetySeconds * 1000); // refresh ~1m before expiry

	refreshTimer = setTimeout(async () => {
		try {
			await refreshToken(kc, safetySeconds);
		} finally {
			// reschedule using the new token
			scheduleRefresh(kc, safetySeconds);
		}
	}, delay);
};

const refreshToken = async (kc: Keycloak, minValidity = 30): Promise<boolean> => {
	if (tokenRefreshPromise) return tokenRefreshPromise;

	tokenRefreshPromise = (async () => {
		try {
			const refreshed = await kc.updateToken(minValidity);
			if (refreshed) {
				logger.debug('[Keycloak] Token refreshed');
				keycloak.set(kc);

				// Refresh user permissions when token is refreshed
				try {
					const { loadUserPermissions } = await import('./permissions');
					await loadUserPermissions();
				} catch (error) {
					logger.debug('[Keycloak] Failed to refresh permissions after token refresh:', error);
				}
			}
			return refreshed;
		} catch (err) {
			logger.debug('[Keycloak] Failed to refresh token:', err);
			return false;
		} finally {
			tokenRefreshPromise = null;
		}
	})();

	return tokenRefreshPromise;
};

export const cleanupKeycloak = () => {
	if (refreshTimer) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
	tokenRefreshPromise = null;
	if (typeof window !== 'undefined') {
		window.removeEventListener('beforeunload', cleanupKeycloak);
	}
};

if (typeof window !== 'undefined') {
	window.addEventListener('beforeunload', cleanupKeycloak);
}

export const initKeycloak = async (): Promise<Keycloak | null> => {
	if (initPromise) return initPromise;

	initPromise = (async () => {
		logger.debug('[Keycloak] Starting initialization');
		try {
			const cfg = await getBackendConfig();

			const kc = new Keycloak({
				url: cfg.keycloak_config.url,
				realm: cfg.keycloak_config.realm,
				clientId: cfg.keycloak_config.clientId
			});

			// Let keycloak-js process any OAuth callback; do not manually parse code/state
			const authenticated = await kc.init({
				onLoad: 'check-sso',
				silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
				pkceMethod: 'S256',
				checkLoginIframe: false
			});

			logger.debug('[Keycloak] Init result:', authenticated);

			// keycloak-js already handled the callback and usually cleans the URL; normalize path regardless
			window.history.replaceState(null, '', window.location.pathname);

			// Hooks
			kc.onTokenExpired = () => {
				// best-effort refresh; scheduleRefresh will also keep us fresh
				refreshToken(kc, 30).catch((e) =>
					logger.error('[Keycloak] onTokenExpired refresh failed:', e)
				);
			};
			kc.onAuthError = (e) => logger.error('[Keycloak] Auth error:', e);

			// Start adaptive refresh
			scheduleRefresh(kc, 60);

			keycloakInstance = kc;
			keycloak.set(kc);

			// Load user permissions after successful authentication
			try {
				const { loadUserPermissions } = await import('./permissions');
				await loadUserPermissions();
			} catch (error) {
				logger.debug('[Keycloak] Failed to load permissions after initialization:', error);
			}

			logger.debug('[Keycloak] Initialization completed successfully');
			return kc;
		} catch (error) {
			logger.error('[Keycloak] Initialization failed:', error);
			keycloakInstance = null;
			keycloak.set(null);
			return null;
		}
	})().finally(() => {
		initPromise = null;
	});

	return initPromise;
};

export const getKeycloak = (): Keycloak | null => keycloakInstance;

export const getValidToken = async (): Promise<string> => {
	const kc = getKeycloak();
	if (!kc || !kc.authenticated) throw new Error('Keycloak is not initialized or authenticated');

	if (kc.isTokenExpired(30)) {
		const ok = await refreshToken(kc, 30);
		if (!ok) throw new Error('Failed to refresh token');
	}
	if (!kc.token) throw new Error('No valid token available');
	return kc.token;
};

export const authFetch = async (url: string, options: RequestInit = {}) => {
	const token = await getValidToken();
	return fetch(url, {
		...options,
		headers: {
			...options.headers,
			Authorization: `Bearer ${token}`
		}
	});
};
